/**
 * 화면(디스플레이) 관리 — 콘솔에서 가장 자주 열리는 화면.
 *
 * 설계 기준은 하나다: **"지금 몇 대가 죽어 있나"에 3초 안에 답한다.**
 * 그래서 요약을 맨 위에 두고, 목록은 상태 → 이름 순으로 훑을 수 있게 배치한다.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LIST_CAP,
  type Display,
  type AgentCommandKind,
  type DisplayLiveStatus,
  type Group,
  type Me,
  type Playlist,
  type Schedule,
  type SignageOneClient,
} from '../api/client.js';
import { behindReason } from '../agent-status.js';
import { absoluteTime, Badge, Empty, ErrorNote, formatBytes, Loading, Modal, Panel, RelTime, rowAction, StatusDot } from '../components/ui.js';
import { lowStorage } from './DisplayDetail.js';
import { DirectSendModal } from '../components/DirectSendModal.js';
import { bulkCommandReport, commandIsDanger, COMMANDS } from '../components/AgentControlForm.js';
import { assignmentName, sortGroups } from '../groups.js';
import { HelpTip } from '../components/HelpTip.js';
import { LocationPicker } from '../components/LocationPicker.js';
import { MaintenancePinForm } from '../components/MaintenancePinForm.js';
import { StorageWarning, useStorageCheck } from '../components/StorageWarning.js';
import { assignmentCheckInput } from '../components/storageCheck.js';
import { useAction, useAsync, useDebounced, usePolling } from '../hooks.js';
import { describePending, suggestedName, type PendingPairing } from './pendingPairings.js';
import { can } from '../capabilities.js';
import { ColumnHead, FilterMenu, FilterNote, useTableSort } from '../components/TableHead.js';
import { useListReturn, useUrlQuery, useUrlText } from '../components/urlState.js';
import {
  activeFilterCount,
  applyFilters,
  filterOptions,
  sortRows,
  versionOf,
  versionRank,
  type FilterOption,
  type Filters,
  type SortDir,
  type SortValue,
} from '../components/tableView.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { FilterField, FilterPopover } from '../components/FilterPopover.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { downloadText, toCsv } from '../components/csv.js';
import { notify } from '../components/Toast.js';
import { Pager, usePaging } from '../components/Pager.js';
import { paginate } from '../components/paging.js';
import { PageSelectionNote, SelectAll, useKeepSelectionWithin } from '../components/SelectionBar.js';
import { assignmentKeyFromPick, PlaybackPicker, pickFromAssignmentKey } from '../components/PlaybackPicker.js';
import { ChevronDown } from 'lucide-react';
import { displayLive } from '../components/displayLive.js';
import { useSiteLabels } from '../components/siteLabelsContext.js';
import { withParticle } from '../siteLabels.js';

/** 목록을 거르는 상태. `attention` 은 **오프라인이거나 오류를 보고한 화면**(서버와 같은 뜻). */
export type DisplayStatusFilter = 'all' | 'online' | 'offline' | 'attention';

/**
 * 주소의 `?status` 읽기.
 *
 * 모르는 값은 전체로 본다 — 사람이 주소를 고쳐 들어오거나 옛 링크가 남아 있을 때,
 * 빈 목록을 내밀고 "화면이 없습니다" 라고 말하는 것보다 전체를 보여주는 편이 낫다.
 */
export function parseStatusParam(value: string | null): DisplayStatusFilter {
  return value === 'online' || value === 'offline' || value === 'attention' ? value : 'all';
}

export type HiddenFilter = 'exclude' | 'include' | 'only';

/** 주소의 `?hidden` 읽기 — 모르는 값은 기본(숨김 제외). */
export function parseHiddenParam(value: string | null): HiddenFilter {
  return value === 'include' || value === 'only' ? value : 'exclude';
}

interface Props {
  client: SignageOneClient;
  me: Me;
}

export const DISPLAY_SORT_KEYS = ['name', 'location', 'group', 'heartbeat', 'storage', 'version'] as const;
export type DisplaySortKey = (typeof DISPLAY_SORT_KEYS)[number];
export type DisplayFilterKey = 'assignment' | 'version';

/**
 * 그룹 열의 이름 — 그룹에 들지 않았으면 null. 받아 온 그룹 목록에 없으면(지웠거나 담당 범위 밖) "(알 수 없는 그룹)".
 */
export function displayGroupName(
  display: Pick<Display, 'group_id'>,
  groups: readonly Pick<Group, 'id' | 'name'>[],
): string | null {
  if (!display.group_id) return null;
  return groups.find((group) => group.id === display.group_id)?.name ?? '(알 수 없는 그룹)';
}

export function displaySortValue(
  display: Display,
  key: DisplaySortKey,
  groups: readonly Pick<Group, 'id' | 'name'>[] = [],
): SortValue {
  switch (key) {
    case 'name':
      return display.name || null;
    case 'location':
      return display.location || null;
    case 'group':
      // 그룹이 없는 화면은 맨 뒤 — 이 열을 누르는 사람은 그룹별로 모아 보려는 중이다.
      return displayGroupName(display, groups);
    case 'heartbeat':
      // 신호를 한 번도 보내지 않은 화면은 가장 오래된 것으로 — 꺼진 화면을 찾는 사람에게 함께 보여야 한다.
      return display.status.last_heartbeat_at ? Date.parse(display.status.last_heartbeat_at) : 0;
    case 'storage':
      return display.status.storage?.free_bytes ?? null;
    case 'version':
      return versionRank(display.status.app_version);
  }
}

export function displayFilterValue(display: Display, key: DisplayFilterKey): string {
  if (key === 'version') return versionOf(display.status.app_version);
  const { kind, playlist_id, schedule_id } = display.assignment;
  if (kind === 'playlist' && playlist_id) return `playlist:${playlist_id}`;
  if (kind === 'schedule' && schedule_id) return `schedule:${schedule_id}`;
  return 'none';
}

/** 편성 거르개의 값 — 이름 순, "그룹 상속 / 없음" 은 맨 뒤. 같은 이름의 플레이리스트와 스케줄은 덧말로 가른다. */
export function assignmentOptions(
  displays: readonly Display[],
  playlists: readonly Pick<Playlist, 'id' | 'name'>[],
  schedules: readonly Pick<Schedule, 'id' | 'name'>[],
  chosen: readonly string[],
): FilterOption[] {
  return filterOptions(
    displays,
    (display) => displayFilterValue(display, 'assignment'),
    (value) => {
      if (value === 'none') return { label: '그룹 상속 / 없음' };
      const [kind, id] = value.split(':');
      if (kind === 'playlist') {
        return { label: playlists.find((p) => p.id === id)?.name ?? '(지운 플레이리스트)', hint: '플레이리스트' };
      }
      return { label: schedules.find((s) => s.id === id)?.name ?? '(지운 스케줄)', hint: '스케줄' };
    },
    chosen,
    (a, b) =>
      Number(a.value === 'none') - Number(b.value === 'none') ||
      a.label.localeCompare(b.label, 'ko', { numeric: true }),
  );
}

/** 버전 거르개의 값 — 높은 버전부터, 보고하지 않는 화면("모름")은 맨 뒤. */
export function versionOptions(displays: readonly Display[], chosen: readonly string[]): FilterOption[] {
  return filterOptions(
    displays,
    (display) => displayFilterValue(display, 'version'),
    (value) => ({ label: value || '모름' }),
    chosen,
    (a, b) => (versionRank(b.value) ?? -1) - (versionRank(a.value) ?? -1),
  );
}

/** 화면 목록 CSV 의 열 — 현장 점검표·자산 대장에 붙여 쓰는 값들. */
export const DISPLAY_CSV_HEADER = [
  '이름',
  '위치',
  '그룹',
  '상태',
  '편성',
  '마지막 신호',
  '남은 공간(GB)',
  '버전',
  '업데이트 채널',
  '고정 버전',
  '디바이스 ID',
  '오류',
] as const;

export function displayCsvRow(
  display: Display,
  playlists: readonly Pick<Playlist, 'id' | 'name'>[],
  schedules: readonly Pick<Schedule, 'id' | 'name'>[],
  groups: readonly Pick<Group, 'id' | 'name'>[] = [],
): Array<string | number | null> {
  const { kind, playlist_id, schedule_id } = display.assignment;
  const assignment =
    kind === 'playlist'
      ? (playlists.find((p) => p.id === playlist_id)?.name ?? '(지운 플레이리스트)')
      : kind === 'schedule'
        ? (schedules.find((s) => s.id === schedule_id)?.name ?? '(지운 스케줄)')
        : '그룹 상속 / 없음';
  const storage = display.status.storage;
  return [
    display.name,
    display.location,
    displayGroupName(display, groups),
    // 끊긴 화면은 오프라인 하나만 — 마지막 보고의 정비 모드·오류는 지금 상태가 아니다(`displayLive`).
    !display.online ? '오프라인' : display.status.maintenance ? '정비 모드' : '온라인',
    assignment,
    display.status.last_heartbeat_at ? absoluteTime(display.status.last_heartbeat_at) : '',
    // 화면의 "남은 공간" 칸(formatBytes)과 같은 단위 — 1024 진법. 둘이 다르면 표와 파일의 숫자가 어긋난다.
    storage ? Math.round((storage.free_bytes / 1024 ** 3) * 10) / 10 : null,
    display.status.app_version,
    display.channel === 'canary' ? '카나리' : '안정',
    display.pinned_version,
    display.device_id,
    displayLive(display).error,
  ];
}

/**
 * 받아 둔 줄에 지금 상태를 얹는다 — 줄의 집합이 그대로이고 아무도 고치지 않았을 때만. 아니면 `null`.
 *
 * 얹는 칸은 상태 따라가기가 주는 것뿐이다(켜짐·마지막 보고·오류·정비 모드·남은 공간·버전·업데이트 단계·
 * 바로 보내기). 이름·위치·편성 같은 칸은 상태로 오지 않는다 — 사람이 바꾸는 것이라, 누가 고친 줄은 고친 시각
 * (`updated_at`)이 받아 둔 것과 달라진다. 그런 줄이 하나라도 있거나 줄이 생기고 사라졌으면 `null` 이고,
 * 부르는 쪽이 목록째 다시 받는다 — 바뀐 이름도, 새 화면도 상태만으로는 그릴 수 없다.
 */
export function mergeDisplayStatus(rows: readonly Display[], fresh: readonly DisplayLiveStatus[]): Display[] | null {
  if (rows.length !== fresh.length) return null;
  const byId = new Map(fresh.map((entry) => [entry.id, entry]));
  const merged: Display[] = [];
  for (const row of rows) {
    const next = byId.get(row.id);
    if (!next) return null;
    if (next.updated_at !== undefined && next.updated_at !== row.updated_at) return null;
    merged.push({
      ...row,
      online: next.online,
      agent: next.agent,
      direct: next.direct,
      status: { ...row.status, ...next.status },
    });
  }
  return merged;
}

export function DisplaysScreen({ client, me }: Props): ReactNode {
  /** 바로 보내기 창 — 보낼 화면(켜진 것)과 오프라인이라 뺀 화면. */
  const [sendingTo, setSendingTo] = useState<{
    targets: Array<{ id: string; name: string }>;
    skipped: Array<{ id: string; name: string }>;
  } | null>(null);
  /*
   * 거르개는 **모두 주소에 산다**(`?status=·q=·loc=·hidden=·plan=·ver=`).
   *
   * 대시보드의 "주의 필요 2" 를 눌렀을 때 거르지 않은 목록이 열리면, 사용자는 그 둘을 눈으로 찾아야
   * 한다 — 숫자를 누른 뜻이 사라진다. 검색·위치·숨김·표 필터도 주소에 둔다: 화면 안의 상태로 두었더니
   * 상세를 열었다 돌아오는 순간 모두 풀려, 다음 화면을 찾으려고 같은 필터를 다시 걸었다.
   */
  const url = useUrlQuery();
  const navigate = useNavigate();
  const siteLabels = useSiteLabels();
  const statusFilter = parseStatusParam(url.get('status'));
  const setStatusFilter = (next: DisplayStatusFilter): void => url.set({ status: next === 'all' ? null : next });
  const [searchText, setSearch] = useUrlText(url, 'q');
  /*
   * 서버에 묻는 검색어는 **입력이 멎은 뒤의 것**이다. 주소는 글자마다 바뀌는데(`useUrlText`), 그대로
   * 조회에 물리면 한 글자 — 한글은 조합 단계마다 — 화면 전부를 쪽 끝까지 다시 받는다. 입력칸은
   * 화면 상태(`searchText`)로 그리므로 늦지 않고, 한글 조합도 끊기지 않는다.
   */
  const search = useDebounced(url.get('q'), 250);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pairingOpen, setPairingOpen] = useState(false);
  /** 여러 대에 건 명령에서 건너뛴 화면 — 까닭별로. 알림 한 줄로는 곧 사라져 목록 위에 남긴다. */
  const [commandReport, setCommandReport] = useState<ReturnType<typeof bulkCommandReport> | null>(null);
  /** 고른 화면의 정비 모드 PIN 을 정하는 창. */
  const [pinOpen, setPinOpen] = useState(false);
  /** 서버가 최종 관문이고, 여기서는 단추를 감출 뿐이다. */
  const canSetPin = me.capabilities.includes('fleet:write');
  /** 화면을 등록·삭제할 수 있는가. */
  const canFleet = can(me, 'fleet:write');
  /** 편성·채널·위치·숨김을 한꺼번에 바꿀 수 있는가. */
  const canEdit = can(me, 'schedule:write');
  /** 위치 이름 바꾸기·지우기 — 담당 범위가 있으면 못 한다(같은 이름을 남의 그룹 화면도 쓴다). */
  /**
   * 숨긴 화면을 볼 것인가.
   *
   * 기본은 감춤이다 — 그러지 않으면 숨겨도 목록이 그대로라 아무 의미가 없다.
   * 되돌리려면 봐야 하므로 전환 수단을 목록 위에 둔다.
   */
  const hiddenFilter = parseHiddenParam(url.get('hidden'));
  const setHiddenFilter = (next: HiddenFilter): void => url.set({ hidden: next === 'exclude' ? null : next });

  /**
   * 위치 거르기. `null` 이면 전체이고, `''` 는 **위치 미지정만** 이다.
   *
   * 거르는 일은 서버가 한다 — 받아 온 쪽에서 추리면 목록이 한 페이지(200대)에서 잘려,
   * 화면이 많아지는 순간 "그 지점에 없는 화면" 이 조용히 생긴다.
   */
  const locationFilter = url.has('loc') ? url.get('loc') : null;
  const setLocationFilter = (next: string | null): void => url.set({ loc: next });
  /** 그룹 거르기(`?group=<id>`). [화면 그룹] 메뉴에서 그룹의 화면 수를 누르면 이것을 걸고 온다. */
  const groupFilter = url.get('group') || null;
  const setGroupFilter = (next: string | null): void => url.set({ group: next });

  const summary = useAsync(() => client.fleetSummary(), [client]);
  /** 목록의 거르개 — 목록과 상태 따라가기가 같은 것을 넘긴다(`displaysStatus`). */
  const listParams = {
    status: statusFilter,
    q: search,
    hidden: hiddenFilter,
    ...(locationFilter === null ? {} : { location: locationFilter }),
    ...(groupFilter === null ? {} : { group_id: groupFilter }),
  };
  const listKey = JSON.stringify(listParams);
  /** 서버에서 거른 목록인가 — 비었을 때 "등록된 화면이 없다" 와 "조건에 맞는 화면이 없다" 를 가른다. */
  const serverFiltered =
    statusFilter !== 'all' || Boolean(search) || locationFilter !== null || groupFilter !== null || hiddenFilter !== 'exclude';
  const displays = useAsync(
    // 쪽을 끝까지 받는다 — 정렬·필터를 브라우저에서 하므로 한 쪽만 받으면 뒤의 화면이 어느 순서로도 안 나온다.
    () => client.allDisplays(listParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 거르개는 내용(listKey)으로 본다.
    [client, listKey],
  );
  const playlists = useAsync(() => client.allPlaylists(), [client]);
  const schedules = useAsync(() => client.listSchedules(), [client]);
  /* 위치 드롭다운이 고를 값. 서버가 세어 준다 — 목록을 받아 추리면 50대에서 잘린다. */
  const locations = useAsync(() => client.listLocations(), [client]);
  /* 그룹 — 거르개·일괄 변경·편성 열이 쓴다. 담당 범위가 있으면 맡은 그룹만 온다. */
  const groups = useAsync(() => client.listGroups(), [client]);
  const groupItems = sortGroups(groups.data?.items ?? []);

  /**
   * 상태 따라가기로 얹은 줄. 목록을 새로 받으면(거르개를 바꿨거나 무언가를 고쳤다) 버린다 — 새것이 기준이다.
   */
  const [live, setLive] = useState<Display[] | null>(null);
  useEffect(() => setLive(null), [displays.data]);
  const items = live ?? displays.data?.items ?? [];

  /*
   * 내가 무언가를 고친 뒤에는 목록째 다시 받는다 — 이름·위치·편성처럼 상태 따라가기가 싣지 않는 칸이 바뀐다.
   *
   * **`reload` 만 붙든다.** `useAsync` 가 돌려주는 상자는 그릴 때마다 새것이라, 상자째 의존하면
   * 이 함수도 그릴 때마다 바뀌고 `usePolling` 이 그때마다 타이머를 다시 건다 — 30초 안에 한 번씩
   * 검색·선택만 해도 주기가 처음부터 다시 세어져 상태가 영영 갱신되지 않았다. `reload` 는 늘 같다.
   */
  const reloadSummary = summary.reload;
  const reloadDisplays = displays.reload;
  const refreshAll = useCallback(() => {
    reloadSummary();
    reloadDisplays();
  }, [reloadSummary, reloadDisplays]);

  /*
   * 함대 상태는 계속 변한다 — 사용자가 새로고침을 누르게 두지 않는다. 다만 **30초마다 목록째 받지 않는다.**
   *
   * 예전에는 그랬다 — 화면이 많은 현장에서 탭 하나가 30초마다 화면 문서 수천 장을 받았다. 이제 지금
   * 바뀌는 칸만 받아(`GET /displays/status`) 받아 둔 줄에 얹는다. 줄이 생기거나 사라졌거나(누가 등록·
   * 숨김·삭제했거나, 켜짐으로 거르는 중에 꺼졌다) 다른 사람이 이름·위치·편성을 고쳤으면(고친 시각이 다르다)
   * 얹을 수 없으니 그때만 목록째 다시 받는다.
   *
   * 묻는 사이 거르개를 바꿨거나 목록을 새로 받았으면 그 답은 버린다 — 옛 거르개의 상태를 새 줄에 얹게 된다.
   */
  const latest = useRef({ rows: items, key: listKey, ready: false });
  latest.current = { rows: items, key: listKey, ready: displays.data !== null && !displays.loading };
  const refreshStatus = useCallback(() => {
    reloadSummary();
    const asked = latest.current;
    if (!asked.ready) return;
    void client
      .displaysStatus(JSON.parse(asked.key) as Parameters<SignageOneClient['displaysStatus']>[0])
      .then((fresh) => {
        const now = latest.current;
        if (now.key !== asked.key || now.rows !== asked.rows) return;
        const merged = mergeDisplayStatus(now.rows, fresh.items);
        if (merged) setLive(merged);
        else reloadDisplays();
      })
      // 따라가기를 못 했으면(옛 서버에 이 경로가 없는 등) 예전처럼 목록째 받는다 — 오류도 거기서 보인다.
      .catch(() => reloadDisplays());
  }, [client, reloadSummary, reloadDisplays]);
  usePolling(refreshStatus, 30_000);

  const action = useAction();
  /** 상세로 가는 길 — 돌아오면 이 목록의 보던 자리로(`useListReturn`). */
  const openDetail = useListReturn(displays.data !== null);
  /** 받는 상한(`LIST_CAP`)을 넘어 뒤가 잘렸는가. 잘렸으면 정렬·거르기도 그 앞부분 안에서다. */
  const truncated = Boolean(displays.data?.next_cursor);

  /*
   * 표의 정렬·거르기 — 받아 온 목록을 브라우저에서 다시 늘어놓는다. 위의 상태·위치·숨김 거르개는
   * 서버가 하고(주소·대시보드에서 지목해 들어온다), 여기는 그 결과 안에서 편성·버전으로 더 좁힌다.
   */
  const [sort, toggleSort] = useTableSort<DisplaySortKey>('displays', DISPLAY_SORT_KEYS, { key: 'name', dir: 'asc' });
  const filters: Filters<DisplayFilterKey> = { assignment: url.list('plan'), version: url.list('ver') };
  const filterKey = filters.assignment!.join(',') + '|' + filters.version!.join(',');
  const rows = useMemo(
    () =>
      sortRows(applyFilters(items, filters, displayFilterValue), sort, (display, key) =>
        displaySortValue(display, key, groupItems),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 필터는 주소에서 매번 새로 읽어 내용(filterKey)으로 본다.
    [items, filterKey, sort, groups.data],
  );
  const filtering = activeFilterCount(filters) > 0;
  /** 그룹 열을 낼 것인가 — 그룹이 하나라도 있거나, 그룹에 든 화면이 보일 때. */
  const showGroup = groupItems.length > 0 || items.some((display) => display.group_id);
  /* 쪽 나누기 — 서버 거르개·표 필터·정렬을 바꾸면 1쪽으로(`Pager`). 보던 쪽은 주소(`?page=`)에. */
  const paging = usePaging(url, 'displays', JSON.stringify([listKey, filterKey, sort.key, sort.dir]));
  const pageSlice = paginate(rows, paging.page, paging.size);
  const rowIds = useMemo(() => rows.map((display) => display.id), [rows]);
  /*
   * 고른 화면은 쪽을 넘겨도 남는다 — 지점 한 곳의 화면을 통째로 고쳐야 한다. 거르개로 목록에서 빠진 화면만 선택에서
   * 뺀다(안 보이는 화면에 일괄 작업이 나가지 않게). 다른 쪽에 남은 수는 막대에 적는다(`PageSelectionNote`).
   */
  useKeepSelectionWithin(rowIds, setSelected);
  const sorting = (dirLabels: Record<SortDir, string>, firstDir: SortDir = 'asc') => ({
    sort,
    onSort: toggleSort,
    dirLabels,
    firstDir,
  });

  /**
   * 거르개를 바꾸면 **보이지 않게 된 화면은 선택에서 뺀다.** 고른 채로 숨으면 일괄 삭제·편성이
   * 눈에 보이지 않는 화면에까지 나간다.
   */
  const changeFilter = (key: DisplayFilterKey, chosen: string[]): void => {
    const next = { ...filters, [key]: chosen };
    url.set(key === 'assignment' ? { plan: chosen } : { ver: chosen });
    const visible = new Set(applyFilters(items, next, displayFilterValue).map((display) => display.id));
    setSelected((current) => new Set([...current].filter((id) => visible.has(id))));
  };

  const toggleSelection = (id: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * 선택한 화면의 업데이트 채널을 바꾼다.
   *
   * 카나리는 "몇 대에서 먼저 본다"는 뜻이고, 그 몇 대를 고르는 자리가 여기다.
   * 목록에서 고를 수 없으면 아무도 카나리를 쓰지 않고, 전 화면 승격만 남는다.
   */
  const setChannelForSelected = (channel: Display['channel']): void => {
    void action.run(
      () => client.bulkUpdateDisplays({ display_ids: [...selected], channel }),
      () => {
        setSelected(new Set());
        refreshAll();
      },
      `화면 ${selected.size}대의 업데이트 채널을 ${channel === 'canary' ? '카나리' : '안정'}로 바꿨습니다`,
    );
  };

  /**
   * 선택한 화면의 정비 모드 PIN 을 한꺼번에 정한다. `null` 은 콘솔 PIN 을 지운다.
   *
   * 지점 하나를 설치 담당자에게 넘길 때 쓴다 — 한 대씩 열어 정하면 스무 대짜리 지점에서
   * 한 대가 반드시 빠지고, 그 화면만 기본 PIN 으로 남는다.
   */
  const setPinForSelected = (pin: string | null): void => {
    void action.run(
      () => client.setMaintenancePin([...selected], pin),
      () => {
        setPinOpen(false);
        setSelected(new Set());
        refreshAll();
      },
      pin === null ? `화면 ${selected.size}대의 콘솔 PIN 을 지웠습니다` : `화면 ${selected.size}대에 정비 모드 PIN 을 정했습니다`,
    );
  };

  /**
   * 선택한 화면을 목록에서 감추거나 되돌린다.
   *
   * 삭제와 나란히 두되 **되돌릴 수 있다는 점이 분명해야 한다.** 목록을 치우려는
   * 사람이 삭제를 누르면 기기가 페어링에서 떨어져 현장 재방문이 된다.
   */
  const setHiddenForSelected = (hidden: boolean): void => {
    void action.run(
      () => client.bulkUpdateDisplays({ display_ids: [...selected], hidden }),
      () => {
        setSelected(new Set());
        refreshAll();
      },
      hidden ? `화면 ${selected.size}대를 목록에서 숨겼습니다` : `화면 ${selected.size}대를 다시 보이게 했습니다`,
    );
  };

  /**
   * 선택한 화면을 삭제한다. **되돌릴 수 없다.**
   *
   * 확인을 한 겹 두는 이유: 기록이 지워지고 디바이스 토큰이 폐기되어, 그 기기는
   * 현장에서 다시 페어링해야 한다. 목록 정리가 목적이었다면 숨기기로 충분하다.
   */
  const deleteSelected = async (): Promise<void> => {
    const names = items
      .filter((display) => selected.has(display.id))
      .map((display) => display.name)
      .slice(0, 5);
    const preview = names.join(', ') + (selected.size > names.length ? ` 외 ${selected.size - names.length}대` : '');
    const ok = await confirmDialog({
      title: `화면 ${selected.size}대를 삭제할까요?`,
      body: `${preview}\n\n되돌릴 수 없으며, 각 기기를 현장에서 다시 페어링해야 합니다. 목록에서만 치우려면 숨기기를 쓰세요.`,
      confirmLabel: '삭제',
      danger: true,
    });
    if (!ok) return;

    void action.run(
      () => client.bulkDeleteDisplays([...selected]),
      () => {
        setSelected(new Set());
        refreshAll();
      },
      `화면 ${selected.size}대를 삭제했습니다`,
    );
  };

  /**
   * 선택한 화면을 한 위치로 옮긴다.
   *
   * 지점을 통째로 정리할 때 쓴다 — 한 대씩 고치면 스무 대짜리 지점에서 오타가 반드시
   * 하나 나고, 그 한 대만 모니터링 벽에서 다른 칸에 떨어진다.
   */
  const setLocationForSelected = (location: string): void => {
    void action.run(
      () => client.bulkUpdateDisplays({ display_ids: [...selected], location }),
      () => {
        setSelected(new Set());
        refreshAll();
        locations.reload();
      },
      location ? `화면 ${selected.size}대를 "${location}" 로 옮겼습니다` : `화면 ${selected.size}대의 위치를 비웠습니다`,
    );
  };

  /**
   * 선택한 화면을 한 그룹에 넣거나(`groupId`) 그룹에서 뺀다(null).
   *
   * 그룹 편성은 자기 편성이 없는 화면에 나가므로, 넣는 순간 재생이 바뀔 수 있다. 그래서 위치처럼 고른 뒤 한 번 더 누른다.
   */
  const setGroupForSelected = (groupId: string | null): void => {
    const name = groupItems.find((group) => group.id === groupId)?.name;
    void action.run(
      () => client.bulkUpdateDisplays({ display_ids: [...selected], group_id: groupId }),
      () => {
        setSelected(new Set());
        refreshAll();
        groups.reload();
      },
      groupId === null ? `화면 ${selected.size}대를 그룹에서 뺐습니다` : `화면 ${selected.size}대를 '${name ?? '그룹'}' 에 넣었습니다`,
    );
  };

  /**
   * 선택한 화면에 원격 명령 — 지점을 통째로 재부팅하거나, 현장이 정비 화면을 열어 둔 채 떠난 화면들을 한 번에 되돌린다.
   *
   * 서버가 걸 수 있는 화면에만 걸고 나머지는 까닭과 함께 돌려준다(꺼짐·옛 에이전트·권한 없음). 건너뛴 화면은
   * 까닭별로 목록 위에 남긴다.
   */
  const commandSelected = async (kind: AgentCommandKind): Promise<void> => {
    const count = selected.size;
    const ok = await confirmDialog({
      title: `${COMMANDS[kind].label} — 화면 ${count}대`,
      body: `선택한 화면 ${count}대에 ${COMMANDS[kind].label} 명령을 보냅니다. ${COMMANDS[kind].note}\n\n꺼져 있거나 그 명령을 모르는 화면은 건너뛰고 까닭을 알려 드립니다.`,
      confirmLabel: COMMANDS[kind].label,
      danger: commandIsDanger(kind),
    });
    if (!ok) return;
    setCommandReport(null);
    void action.run(
      () => client.bulkDisplayCommand([...selected], kind),
      (result) => {
        const report = bulkCommandReport(result);
        setCommandReport(report.skipped.length > 0 ? report : null);
        setSelected(new Set());
        refreshAll();
      },
      (result) => bulkCommandReport(result).summary,
    );
  };

  /** 고른 화면의 음소거를 한꺼번에 — 사무실 층 하나를 조용하게. 화면마다의 음량은 그대로 둔다. */
  const setAudioForSelected = (muted: boolean): void => {
    void action.run(
      () => client.bulkUpdateDisplays({ display_ids: [...selected], audio: { muted } }),
      () => {
        setSelected(new Set());
        refreshAll();
      },
      muted ? `화면 ${selected.size}대를 음소거했습니다` : `화면 ${selected.size}대의 음소거를 풀었습니다`,
    );
  };

  const assignToSelected = (assignment: Display['assignment']): void => {
    void action.run(
      () => client.bulkUpdateDisplays({ display_ids: [...selected], assignment }),
      () => {
        setSelected(new Set());
        refreshAll();
      },
      `화면 ${selected.size}대에 편성을 배정했습니다`,
    );
  };

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {displays.error ? <ErrorNote message={displays.error} /> : null}

      <Panel
        title="화면"
        className="screen-fill"
        actions={
          <>
            {/* 요약을 제목 옆에 둔다 — 위에 따로 띄우면 같은 숫자를 두 번 말하게 된다. */}
            <FleetSummary summary={summary.data} loading={summary.loading} />
            <span className="mx-1 hidden h-4 w-px bg-slate-200 sm:block" />
            <input
              className="input input-search w-full sm:w-auto"
              type="search"
              placeholder="이름·위치·디바이스 ID·MAC 검색"
              value={searchText}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className="input"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
              aria-label="상태 필터"
            >
              <option value="all">전체 상태</option>
              <option value="online">온라인</option>
              <option value="offline">오프라인</option>
              {/* 대시보드의 "주의 필요" 가 보내는 자리. 목록에서도 직접 고를 수 있어야 한다. */}
              <option value="attention">주의 필요</option>
            </select>
            {/*
              위치·숨김은 [필터] 뒤로, CSV 는 [⋯] 뒤로 접는다(점진적 공개) — 머리에는 늘 쓰는 검색·상태만.
              걸린 거르개 수는 단추에 남는다(대시보드의 위치 카드가 `?loc=` 로 이 화면을 열 때도 보인다).

              위치 고를 값은 서버가 세어 준다(`/displays/locations`) — 목록을 받아 추리면
              화면이 많을 때 뒤쪽 지점이 드롭다운에서 조용히 빠진다.
            */}
            <FilterPopover
              activeCount={(locationFilter !== null ? 1 : 0) + (groupFilter !== null ? 1 : 0) + (hiddenFilter !== 'exclude' ? 1 : 0)}
              onReset={() => url.set({ loc: null, group: null, hidden: null })}
            >
              <FilterField label="위치">
                <select
                  className="input"
                  value={locationFilter ?? '__all__'}
                  onChange={(event) =>
                    setLocationFilter(event.target.value === '__all__' ? null : event.target.value)
                  }
                  aria-label="위치 필터"
                >
                  <option value="__all__">전체 위치</option>
                  <option value="">위치 미지정</option>
                  {(locations.data?.items ?? []).map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name} ({item.display_count})
                    </option>
                  ))}
                </select>
              </FilterField>
              {/* 그룹이 하나도 없으면 고를 것이 없다 — 칸을 내지 않는다(걸려 있으면 풀 수 있게 남긴다). */}
              {groupItems.length > 0 || groupFilter !== null ? (
                <FilterField label="그룹">
                  <select
                    className="input"
                    value={groupFilter ?? '__all__'}
                    onChange={(event) => setGroupFilter(event.target.value === '__all__' ? null : event.target.value)}
                    aria-label="그룹 필터"
                  >
                    <option value="__all__">전체 그룹</option>
                    {groupFilter !== null && !groupItems.some((group) => group.id === groupFilter) ? (
                      <option value={groupFilter}>(지운 그룹)</option>
                    ) : null}
                    {groupItems.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.display_count})
                      </option>
                    ))}
                  </select>
                </FilterField>
              ) : null}
              <FilterField label="숨긴 화면">
                <select
                  className="input"
                  value={hiddenFilter}
                  onChange={(event) => setHiddenFilter(event.target.value as typeof hiddenFilter)}
                  aria-label="숨김 필터"
                >
                  <option value="exclude">숨김 제외</option>
                  <option value="include">숨김 포함</option>
                  <option value="only">숨김만</option>
                </select>
              </FilterField>
            </FilterPopover>
            {/* 보이는 대로(필터·정렬 그대로) 내려받는다 — 현장 점검표·자산 대장에 붙여 쓴다. */}
            <MoreMenu
              size="normal"
              label="화면 목록 더 보기"
              items={[
                {
                  label: 'CSV 내려받기',
                  disabled: rows.length === 0,
                  title: '지금 조건의 화면 전부(필터·정렬 그대로, 모든 쪽)를 CSV 로 내려받습니다',
                  onSelect: () => {
                    const today = absoluteTime(new Date().toISOString()).slice(0, 10);
                    downloadText(
                      `화면-목록-${today}.csv`,
                      toCsv(
                        DISPLAY_CSV_HEADER,
                        rows.map((display) =>
                          displayCsvRow(display, playlists.data?.items ?? [], schedules.data?.items ?? [], groupItems),
                        ),
                      ),
                    );
                    notify(`화면 ${rows.length}대를 CSV 로 내려받았습니다`);
                  },
                },
                // 그룹 관리는 제 메뉴(화면 그룹)로 뗐다 — 여기서는 그리로 가는 길만 남긴다.
                {
                  label: '화면 그룹 관리',
                  title: '그룹을 만들고 이름·그룹 편성을 고칩니다 — 캠페인 대상·담당 범위로 씁니다',
                  onSelect: () => navigate('/groups'),
                },
                // 위치 관리도 제 메뉴(사업장·위치)로 뗐다(2026-10-21) — 지역·사업장과 이름 바꾸기·지우기를 한 표에서 한다.
                {
                  label: '사업장·위치 관리',
                  title: `위치마다 ${siteLabels.region}·${withParticle(siteLabels.site, '을')} 정하고, 위치 이름을 바꾸거나 지웁니다`,
                  onSelect: () => navigate('/locations'),
                },
              ]}
            />
            {/* 등록은 `fleet:write` 다 — 할 수 없는 사람에게 내밀지 않는다. */}
            {canFleet ? (
              <button type="button" className="btn btn-primary" onClick={() => setPairingOpen(true)}>
                화면 추가
              </button>
            ) : null}
          </>
        }
      >
        {selected.size > 0 ? (
          <BulkBar
            client={client}
            selectedIds={[...selected]}
            count={selected.size}
            playlists={playlists.data?.items ?? []}
            schedules={schedules.data?.items ?? []}
            pending={action.pending}
            locations={(locations.data?.items ?? []).map((item) => item.name)}
            onAssign={assignToSelected}
            onChannel={setChannelForSelected}
            onLocation={setLocationForSelected}
            groups={groupItems}
            onGroup={setGroupForSelected}
            onAudio={setAudioForSelected}
            canUngroup={!me.scope}
            canEdit={canEdit}
            canDelete={canFleet}
            onPin={canSetPin ? () => setPinOpen(true) : undefined}
            /*
              바로 보내기는 켜진 화면에 곧바로 나가고, 꺼진 화면은 창에서 **걸어 둘지** 고른다(서버의
              `queue_offline`). 걸어 둔 것은 다시 연결되면 나가고 그 전까지 '전달 대기' 로 보인다.
            */
            sendBlocked={undefined}
            onSend={() => {
              const chosen = items.filter((display) => selected.has(display.id));
              const pick = (display: Display) => ({ id: display.id, name: display.name });
              setSendingTo({
                targets: chosen.filter((display) => display.online).map(pick),
                skipped: chosen.filter((display) => !display.online).map(pick),
              });
            }}
            onHide={setHiddenForSelected}
            onCommand={(kind) => void commandSelected(kind)}
            onDelete={deleteSelected}
            showUnhide={hiddenFilter !== 'exclude'}
            onCancel={() => setSelected(new Set())}
            note={
              <PageSelectionNote
                pageIds={pageSlice.rows.map((display) => display.id)}
                allIds={rowIds}
                selected={selected}
                unit="대"
                onChange={setSelected}
              />
            }
          />
        ) : null}

        {commandReport ? (
          <div className="filter-note" role="status">
            <span>
              {commandReport.summary}
              {commandReport.skipped.map((group) => (
                <span key={group.message} className="block">
                  {group.message} — {group.names.slice(0, 8).join(', ')}
                  {group.names.length > 8 ? ` 외 ${group.names.length - 8}대` : ''}
                </span>
              ))}
            </span>
            <button type="button" className="link" onClick={() => setCommandReport(null)}>
              닫기
            </button>
          </div>
        ) : null}

        {pinOpen && selected.size > 0 ? (
          <Modal title={`정비 모드 PIN — ${selected.size}대`} onClose={() => setPinOpen(false)}>
            <div className="flex flex-col gap-3 sm:p-4">
              <p className="hint">선택한 화면 모두에 같은 PIN 을 적용합니다.</p>
              <MaintenancePinForm
                pending={action.pending}
                canClear
                onSave={(pin) => setPinForSelected(pin)}
                onClear={() => setPinForSelected(null)}
              />
            </div>
          </Modal>
        ) : null}

        {displays.loading && items.length === 0 ? <Loading /> : null}

        {/*
          거르개(상태·검색·위치·그룹·숨김)에 걸려 비었으면 그렇게 말한다. "등록된 화면이 없습니다" 라고 하면 그룹에서
          화면을 다 뺀 직후처럼 거른 결과가 빈 것을 화면이 사라진 것으로 읽는다.
        */}
        {!displays.loading && items.length === 0 && serverFiltered ? (
          <Empty
            title="조건에 맞는 화면이 없습니다"
            hint="검색어나 필터를 바꾸거나 풀어 보세요."
            action={
              <button
                type="button"
                className="btn"
                onClick={() => url.set({ status: null, q: null, loc: null, group: null, hidden: null })}
              >
                필터 풀기
              </button>
            }
          />
        ) : null}
        {!displays.loading && items.length === 0 && !serverFiltered ? (
          <Empty
            title="등록된 화면이 없습니다"
            hint={
              canFleet
                ? '플레이어 화면에 표시된 6자리 코드를 [화면 추가]에서 입력하세요.'
                : '화면 등록에는 화면 관리 권한이 필요합니다.'
            }
            action={
              canFleet ? (
                <button type="button" className="btn btn-primary" onClick={() => setPairingOpen(true)}>
                  화면 추가
                </button>
              ) : undefined
            }
          />
        ) : null}

        {truncated ? (
          <div className="filter-note" role="status">
            <span>
              화면이 {LIST_CAP.toLocaleString('ko-KR')}대를 넘어 이름 순으로 앞의 {items.length.toLocaleString('ko-KR')}대만
              보입니다. 정렬·필터도 그 안에서만 됩니다 — 검색이나 위치로 좁혀 보세요.
            </span>
          </div>
        ) : null}

        {filtering && items.length > 0 ? (
          <FilterNote shown={rows.length} total={items.length} unit="대" onClear={() => url.set({ plan: null, ver: null })} />
        ) : null}

        {items.length > 0 ? (
          /*
            표만 옆으로 민다. 패널 본문 전체가 밀리면 위의 일괄 막대까지 함께 밀려,
            화면을 고른 뒤 버튼을 찾으러 다시 돌아와야 한다.
          */
          <div className="table-scroll">
            <table className="table min-w-[40rem]">
              <thead>
                <tr>
                  <th className="col-check">
                    {/*
                      이 쪽의 줄만 고른다 — 걸러져 숨은 화면까지 고르면 일괄 작업이 보이지 않는 곳에 나간다. 조건에 맞는 전부는
                      막대의 [조건에 맞는 N대 모두 선택] 으로.
                    */}
                    <SelectAll ids={pageSlice.rows.map((display) => display.id)} selected={selected} onChange={setSelected} />
                  </th>
                  <ColumnHead label="이름" column="name" sorting={sorting({ asc: '가나다순', desc: '가나다 역순' })} />
                  <ColumnHead label="위치" column="location" sorting={sorting({ asc: '가나다순', desc: '가나다 역순' })} />
                  {/* 그룹을 쓰지 않는 고객사에는 열을 내지 않는다 — 줄마다 "—" 만 서는 열은 읽을 것 없이 폭만 차지한다. */}
                  {showGroup ? (
                    <ColumnHead label="그룹" column="group" sorting={sorting({ asc: '가나다순', desc: '가나다 역순' })} />
                  ) : null}
                  <ColumnHead
                    label="편성"
                    column="assignment"
                    filter={
                      <FilterMenu
                        label="편성"
                        options={assignmentOptions(
                          items,
                          playlists.data?.items ?? [],
                          schedules.data?.items ?? [],
                          filters.assignment ?? [],
                        )}
                        chosen={filters.assignment ?? []}
                        onChange={(chosen) => changeFilter('assignment', chosen)}
                      />
                    }
                  />
                  {/* 오래된 순이 먼저다 — 이 열을 누르는 사람은 꺼진 화면을 찾는 중이다. */}
                  <ColumnHead
                    label="마지막 신호"
                    column="heartbeat"
                    sorting={sorting({ asc: '오래된 순', desc: '최근 순' })}
                  />
                  {/*
                    남은 저장공간.

                    **여러 대를 한눈에 훑어야 하는 값이다.** 상세를 하나씩 열어 보게 하면
                    바닥나는 기기를 미리 찾는 일이 사실상 안 된다 — 공간이 차면 웹뷰가 받아 둔
                    파일을 임의로 버려 재생이 조용히 구멍 나고, 그때는 이미 늦다.
                  */}
                  <ColumnHead label="남은 공간" column="storage" sorting={sorting({ asc: '적은 순', desc: '많은 순' })} />
                  {/* 낮은 버전이 먼저다 — 롤아웃을 올렸는데 남아 있는 화면을 찾는 열이다. */}
                  <ColumnHead
                    label="버전"
                    column="version"
                    sorting={sorting({ asc: '낮은 버전 순', desc: '높은 버전 순' })}
                    filter={
                      <FilterMenu
                        label="버전"
                        options={versionOptions(items, filters.version ?? [])}
                        chosen={filters.version ?? []}
                        onChange={(chosen) => changeFilter('version', chosen)}
                      />
                    }
                  />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr className="row-empty">
                    <td colSpan={showGroup ? 8 : 7}>필터 조건에 맞는 화면이 없습니다.</td>
                  </tr>
                ) : null}
                {pageSlice.rows.map((display) => (
                  <tr key={display.id} {...rowAction(() => openDetail(`/displays/${display.id}`))}>
                    <td className="col-check" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`${display.name} 선택`}
                        checked={selected.has(display.id)}
                        onChange={() => toggleSelection(display.id)}
                      />
                    </td>
                    {/*
                      상태·이름·위치를 한 칸에 모은다. 열을 나누면 한 화면을 읽는 데
                      눈이 표를 가로질러야 하고, 화면이 많아질수록 그 비용이 커진다.
                    */}
                    <td>
                      <span className="flex items-center gap-2">
                        <StatusDot online={display.online} />
                        <span className="cell-strong">{display.name || '(이름 없음)'}</span>
                        {display.direct && !display.direct.expired ? (
                          display.direct.delivered_at === null ? (
                            <Badge tone="warn">바로 보내기 · 전달 대기</Badge>
                          ) : (
                            <Badge tone="brand">바로 보내기</Badge>
                          )
                        ) : null}
                        {/*
                          숨김 목록을 볼 때 어느 것이 숨겨진 것인지 표시한다.
                          "숨김 포함" 으로 보면 두 종류가 섞이는데, 구분이 없으면
                          되돌릴 대상을 고를 수 없다.
                        */}
                        {display.hidden ? <Badge tone="neutral">숨김</Badge> : null}
                        {/* 현장에서 열어 둔 채 떠나면 재생이 멈춘 채 남는다 — 원격으로 풀 수 없어 목록에서라도 드러낸다. */}
                        {/* 끊긴 화면은 점이 오프라인을 말한다 — 마지막 보고에 남은 정비 모드·오류는 지금 상태가 아니라 걷는다(`displayLive`). */}
                        {displayLive(display).maintenance ? <Badge tone="warn">정비 모드</Badge> : null}
                      </span>
                      {/* 오류는 이름 아래에 남긴다 — 어느 화면의 오류인지가 이름에 붙어야 한다. */}
                      {displayLive(display).error ? (
                        <span className="mt-0.5 block pl-4 text-[11.5px] text-red-500">
                          {displayLive(display).error}
                        </span>
                      ) : null}
                    </td>
                    {/*
                      위치를 제 열로 뺐다. 이름 아래 작은 글씨로 두었더니 지점별로 훑을 수
                      없었고, 위치로 거르는 일이 생기면서 거른 값이 어디에 걸렸는지도
                      한눈에 보여야 한다.
                    */}
                    <td className="cell-muted">{display.location || '—'}</td>
                    {/*
                      그룹 — 그룹 편성·캠페인 대상·담당 범위가 모두 이것을 쓴다(2026-10-21). 예전에는 자기 편성이 없는 화면의
                      편성 칸에만 "· 그룹 X" 로 묻어 나와, 편성을 따로 준 화면은 어느 그룹인지 상세를 열어야 알았다.
                    */}
                    {showGroup ? (
                      <td className={display.group_id ? undefined : 'cell-muted'}>
                        {groups.data ? (displayGroupName(display, groupItems) ?? '—') : ''}
                      </td>
                    ) : null}
                    <td>
                      <AssignmentLabel
                        assignment={display.assignment}
                        group={groupItems.find((group) => group.id === display.group_id) ?? null}
                        playlists={playlists.data?.items ?? []}
                        schedules={schedules.data?.items ?? []}
                      />
                    </td>
                    <td className="cell-muted"><RelTime iso={display.status.last_heartbeat_at} /></td>
                    {/*
                      보고하지 않는 기기는 "—" 다. **0 으로 적지 않는다** — 옛 에이전트와
                      셸 없는 브라우저 키오스크는 여유를 모르고, 0 은 "없다" 라 뜻이 다르다.
                    */}
                    <td className={lowStorage(display.status.storage) ? 'font-medium text-amber-700' : 'cell-muted'}>
                      {display.status.storage ? formatBytes(display.status.storage.free_bytes) : '—'}
                    </td>
                    {/*
                      버전 옆에 고정·카나리를 붙인다. 롤아웃을 올렸는데 안 올라가는 화면을
                      찾는 것이 이 열의 쓸모다 — 고정은 채널을 이기고, 카나리는 다른 버전을 본다.
                    */}
                    <td className="cell-muted">
                      {display.status.app_version ?? '—'}
                      {display.pinned_version ? (
                        <Badge tone="warn">v{display.pinned_version} 고정</Badge>
                      ) : display.channel === 'canary' ? (
                        <Badge tone="neutral">canary</Badge>
                      ) : null}
                      {/*
                        **받아야 할 버전보다 낮은 화면.**

                        승격은 화면이 물어봐야 일어난다. 물지 않는 화면은 아무 소리도 내지 않아,
                        예전에는 서버 로그를 뒤져야만 알 수 있었다 — 그사이 한 대가 네 버전을
                        건너뛴 채 돌고 있었다. 고정해 둔 화면은 그 버전이 목표라 여기 걸리지 않는다.
                      */}
                      {(() => {
                        // 이유마다 할 일이 다르다 — 한 덧얰리로 적으면 운영자가 매번 셋 다 해 보게 된다.
                        const reason = behindReason(display.agent);
                        if (!reason) return null;
                        return (
                          <span title={reason.hint}>
                            <Badge tone={reason.tone}>{reason.label}</Badge>
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <Pager slice={pageSlice} paging={paging} unit="대" />
      </Panel>

      {sendingTo ? (
        <DirectSendModal
          client={client}
          targets={sendingTo.targets}
          skipped={sendingTo.skipped}
          onClose={() => setSendingTo(null)}
          onSent={() => {
            setSendingTo(null);
            setSelected(new Set());
            refreshAll();
          }}
        />
      ) : null}

      {pairingOpen ? (
        <PairingModal
          client={client}
          locations={(locations.data?.items ?? []).map((item) => item.name)}
          onClose={() => setPairingOpen(false)}
          onPaired={() => {
            setPairingOpen(false);
            refreshAll();
            locations.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * 함대 요약 — 한 줄.
 *
 * 예전에는 큰 숫자 상자 다섯 개를 가로로 늘어놓았다. 화면이 한 대인 곳에서는
 * 그 중 셋이 0 이고, 0 세 개가 목록보다 큰 자리를 차지했다. 대시보드가 이미
 * 같은 숫자를 보여주므로 여기서는 **지금 손대야 할 것만** 말한다.
 *
 * 문제가 없으면 조용히 "모두 정상" 한 줄이다. 배정 없는 화면을 굳이 남겨 두는
 * 이유는 그것이 **조용한 손실**이기 때문이다 — 켜져 있고 온라인인데 아무것도
 * 안 나온다. 오프라인보다 발견이 늦다.
 */
function FleetSummary({
  summary,
  loading,
}: {
  summary: { total: number; online: number; offline: number; unassigned: number; erroring: number } | null;
  loading: boolean;
}): ReactNode {
  if (loading && !summary) return <Loading />;
  if (!summary) return null;

  const problems: Array<{ label: string; value: number; tone: string }> = [
    { label: '오프라인', value: summary.offline, tone: 'text-red-600' },
    { label: '오류 보고', value: summary.erroring, tone: 'text-red-600' },
    { label: '배정 없음', value: summary.unassigned, tone: 'text-amber-600' },
  ].filter((entry) => entry.value > 0);

  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13.5px]">
      <span className="text-slate-700">
        화면 <strong className="font-mono font-semibold text-slate-900">{summary.total}</strong>대 · 온라인{' '}
        <strong className="font-mono font-semibold text-slate-900">{summary.online}</strong>
      </span>
      {problems.length === 0 ? (
        <span className="flex items-center gap-1.5 text-emerald-600">
          <StatusDot online />
          모두 정상
        </span>
      ) : (
        problems.map((entry) => (
          <span key={entry.label} className={entry.tone}>
            {entry.label} <strong className="font-mono font-semibold">{entry.value}</strong>
          </span>
        ))
      )}
    </div>
  );
}

function AssignmentLabel({
  assignment,
  group,
  playlists,
  schedules,
}: {
  assignment: Display['assignment'];
  /** 화면이 든 그룹 — 자기 편성이 없으면 그룹 편성이 나간다. */
  group: Group | null;
  playlists: Playlist[];
  schedules: Schedule[];
}): ReactNode {
  if (assignment.kind === 'playlist') {
    const name = playlists.find((p) => p.id === assignment.playlist_id)?.name;
    return <Badge tone="info">{name ?? '플레이리스트'}</Badge>;
  }
  if (assignment.kind === 'schedule') {
    const name = schedules.find((s) => s.id === assignment.schedule_id)?.name;
    return <Badge tone="info">{name ?? '스케줄'}</Badge>;
  }
  /*
   * 자기 편성이 없는 화면 — 그룹 편성이 있으면 그것이 나간다. "그룹 상속 / 없음" 한 마디로 두면 둘 중 무엇인지
   * 상세를 열어 봐야 알았다.
   */
  const inherited = group ? assignmentName(group.assignment, playlists, schedules) : null;
  // 어느 그룹인지는 옆의 그룹 열이 말한다 — 여기서는 "그룹의 편성을 따른다" 만.
  if (group && inherited) {
    return (
      <span className="cell-muted" title={`그룹 '${group.name}' 의 편성을 따릅니다`}>
        {inherited} · 그룹 편성
      </span>
    );
  }
  return (
    <span className="cell-muted" title={group ? `그룹 '${group.name}' 에도 편성이 없습니다` : '배정한 편성도, 그룹도 없습니다'}>
      없음
    </span>
  );
}

function BulkBar({
  client,
  selectedIds,
  count,
  playlists,
  schedules,
  pending,
  locations,
  onAssign,
  onChannel,
  onLocation,
  groups,
  onGroup,
  onAudio,
  canUngroup,
  onPin,
  onSend,
  sendBlocked,
  onHide,
  onCommand,
  onDelete,
  showUnhide,
  canEdit,
  canDelete,
  onCancel,
  note,
}: {
  client: SignageOneClient;
  /** 고른 화면 — 고른 편성이 이 화면들의 저장 공간에 들어가는지 묻는다. */
  selectedIds: string[];
  count: number;
  playlists: Playlist[];
  schedules: Schedule[];
  pending: boolean;
  /** 등록되어 있는 위치 이름들. */
  locations: string[];
  onAssign: (assignment: Display['assignment']) => void;
  onChannel: (channel: Display['channel']) => void;
  onLocation: (location: string) => void;
  /** 넣을 수 있는 그룹 — 담당 범위가 있으면 맡은 그룹만. */
  groups: readonly Group[];
  /** 고른 화면을 그룹에 넣는다. null 은 그룹에서 빼기. */
  onGroup: (groupId: string | null) => void;
  /** 고른 화면의 음소거를 켜고 끈다 — 화면마다의 음량은 그대로. */
  onAudio: (muted: boolean) => void;
  /** 그룹에서 뺄 수 있는가 — 담당 범위가 있으면 못 한다(빼는 순간 그 화면이 자기 범위를 떠난다, 서버도 403). */
  canUngroup: boolean;
  /** 정비 모드 PIN 창을 연다. 권한이 없으면 undefined 라 단추가 나오지 않는다. */
  onPin?: () => void;
  onSend: () => void;
  /** 바로 보낼 수 없는 까닭 — 고른 화면이 모두 오프라인이면 단추를 막고 이 말을 띄운다. */
  sendBlocked?: string;
  onHide: (hidden: boolean) => void;
  /** 고른 화면에 원격 명령 — 화면 관리 권한(`canDelete`)이 있을 때만 [⋯] 에 나온다. */
  onCommand: (kind: AgentCommandKind) => void;
  onDelete: () => void;
  /** 숨김이 보이는 목록에서만 [숨김 해제] 를 낸다 — 안 보이는 것을 되돌릴 일은 없다. */
  showUnhide: boolean;
  /** 편성·채널·위치·숨김을 바꿀 수 있는가(`schedule:write`). 없으면 그 단추들이 나오지 않는다. */
  canEdit: boolean;
  /** 화면을 지울 수 있는가(`fleet:write`). */
  canDelete: boolean;
  onCancel: () => void;
  /** 수 바로 뒤의 덧말 — 다른 쪽에 고른 화면(`PageSelectionNote`). */
  note?: ReactNode;
}): ReactNode {
  const [value, setValue] = useState('');
  /** 옮겨 갈 위치. 고르기만 하고 적용은 버튼이 한다 — 드롭다운은 스치기만 해도 바뀐다. */
  const [location, setLocation] = useState('');
  /** 옮겨 갈 그룹 — 고르기만 하고 적용은 버튼이 한다(그룹 편성이 나가기 시작할 수 있다). `__none__` 은 빼기. */
  const [groupChoice, setGroupChoice] = useState('');
  // 펼친 채로 연다(2026-10-15 사용자 지시) — 채널·위치·정비 PIN 도 고르자마자 보인다.
  const [moreOpen, setMoreOpen] = useState(true);
  const moreId = useId();
  /*
   * 고른 편성이 고른 화면들에 들어가는가 — [일괄 배정] 을 누르기 **전에** 말한다. 고르기와 적용이 나뉘어
   * 있는 자리라 여기서 말해야 누르기 전에 뺄 화면을 고를 수 있다. 막지는 않는다.
   */
  const storageShortfalls = useStorageCheck(client, assignmentCheckInput(selectedIds, value));

  const apply = (): void => {
    if (!value) return;
    const [kind, id] = value.split(':') as ['playlist' | 'schedule', string];
    onAssign({
      kind,
      playlist_id: kind === 'playlist' ? id : null,
      schedule_id: kind === 'schedule' ? id : null,
    });
  };

  return (
    <>
    <div className="bulkbar">
      <span className="bulkbar-count">{count}대 선택됨</span>
      {note}
      {canEdit ? (
      <>
      <PlaybackPicker
        className="pp-bar"
        label="일괄 편성"
        placeholder="편성 선택…"
        value={pickFromAssignmentKey(value)}
        sources={{ playlists, schedules }}
        onChange={(next) => setValue(assignmentKeyFromPick(next))}
      />
      <button type="button" className="btn btn-primary" disabled={!value || pending} onClick={apply}>
        {pending ? '적용 중…' : '일괄 배정'}
      </button>
      {/* 편성을 바꾸는 것과 지금 띄우는 것은 다른 일이라 버튼을 나눈다. */}
      <button type="button" className="btn" disabled={pending || Boolean(sendBlocked)} title={sendBlocked} onClick={onSend}>
        바로 보내기
      </button>
      {/*
        채널·위치·정비 PIN 은 가끔 하는 일이라 [기타 일괄 변경] 을 눌러야 다음 줄에 나온다(점진적 공개) —
        고르자마자 칸 열한 개가 한 줄에 서면 무엇부터 할지 보이지 않는다.
      */}
      <button
        type="button"
        className="btn"
        aria-expanded={moreOpen}
        aria-controls={moreId}
        onClick={() => setMoreOpen((open) => !open)}
      >
        기타 일괄 변경
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
      </button>
      </>
      ) : null}

      {/* 좁은 화면에서는 선 대신 줄을 바꾼다 — 치우는 버튼들이 다음 줄에 따로 모인다. */}
      <span className="mx-1 h-0 w-px basis-full bg-slate-200 sm:h-4 sm:basis-auto" />

      {/*
        숨기기와 삭제를 나란히 두되 무게를 다르게 준다.

        둘 다 "목록에서 없앤다" 로 보이지만 결과가 전혀 다르다 — 숨기기는 되돌릴 수
        있고 화면은 계속 재생하지만, 삭제는 기기를 페어링에서 떼어내 현장 재방문이 된다.
        목록 정리가 목적인 사람이 삭제를 누르지 않도록 숨기기를 먼저, 평범하게 둔다.
      */}
      {canEdit ? (
        <button type="button" className="btn" disabled={pending} onClick={() => onHide(true)}>
          숨기기
        </button>
      ) : null}
      {canEdit && showUnhide ? (
        <button type="button" className="btn" disabled={pending} onClick={() => onHide(false)}>
          숨김 해제
        </button>
      ) : null}
      {/*
        원격 명령과 삭제는 [⋯] 뒤에 — 둘 다 화면 관리 권한(fleet:write)이고, 삭제는 기기를 페어링에서 떼어내 현장
        재방문이 되는 일이다. 명령은 꺼진 화면·그 명령을 모르는 화면을 서버가 건너뛰고 까닭을 돌려준다.
      */}
      {canDelete ? (
        <MoreMenu
          size="normal"
          label="선택한 화면 작업 더 보기"
          items={[
            ...(['reboot', 'exit_maintenance', 'quit', 'power_off'] as const).map((kind) => ({
              label: `${COMMANDS[kind].label}…`,
              danger: kind === 'quit' || kind === 'power_off',
              disabled: pending,
              title: COMMANDS[kind].note,
              onSelect: () => onCommand(kind),
            })),
            {
              label: '삭제',
              danger: true,
              disabled: pending,
              title: '되돌릴 수 없습니다. 목록에서만 치우려면 숨기기를 쓰세요.',
              onSelect: onDelete,
            },
          ]}
        />
      ) : null}

      <button type="button" className="link" onClick={onCancel}>
        선택 해제
      </button>
    </div>
    {canEdit && moreOpen ? (
      <div id={moreId} className="bulkbar bulkbar-sub">
        {/*
          채널은 고른 즉시 바뀐다. 되돌릴 수 있는 조작이고(다음에 받을 버전을 정할 뿐이다),
          카나리 대상을 추리는 일은 몇 번씩 고쳐 잡게 되므로 단계를 두지 않는다.
        */}
        <select
          className="input max-w-full"
          value=""
          disabled={pending}
          aria-label="일괄 업데이트 채널"
          onChange={(event) => {
            if (event.target.value) onChannel(event.target.value as Display['channel']);
          }}
        >
          <option value="">업데이트 채널…</option>
          <option value="canary">카나리</option>
          <option value="stable">안정</option>
        </select>
        {/*
          위치 일괄 변경.

          고른 즉시 적용하지 않는다. 드롭다운은 스치기만 해도 값이 바뀌는데, 여기서
          바뀌는 것은 **여러 대의 위치**라 잘못 스친 것을 되돌리려면 원래 무엇이었는지를
          화면마다 기억하고 있어야 한다. 고르고 나서 한 번 더 누르게 한다.
        */}
        <LocationPicker
          value={location}
          known={locations}
          disabled={pending}
          ariaLabel="일괄 위치"
          onChange={setLocation}
        />
        <button type="button" className="btn" disabled={pending} onClick={() => onLocation(location)}>
          위치 적용
        </button>
        {/* 소리 — 고른 즉시 바뀐다(되돌릴 수 있다, 채널과 같은 까닭). 음량은 화면 설정에서 한 대씩. */}
        <select
          className="input max-w-full"
          value=""
          disabled={pending}
          aria-label="일괄 소리"
          onChange={(event) => {
            if (event.target.value) onAudio(event.target.value === 'mute');
          }}
        >
          <option value="">소리…</option>
          <option value="mute">음소거</option>
          <option value="unmute">음소거 해제</option>
        </select>
        {/* 그룹 — 그룹이 없으면 [화면 그룹] 메뉴에서 먼저 만든다. 고르고 한 번 더 누른다(위치와 같은 까닭). */}
        {groups.length > 0 ? (
          <>
            <select
              className="input max-w-full"
              value={groupChoice}
              disabled={pending}
              aria-label="일괄 그룹"
              onChange={(event) => setGroupChoice(event.target.value)}
            >
              <option value="">그룹 선택…</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
              {canUngroup ? <option value="__none__">그룹에서 빼기</option> : null}
            </select>
            <button
              type="button"
              className="btn"
              disabled={pending || groupChoice === ''}
              onClick={() => onGroup(groupChoice === '__none__' ? null : groupChoice)}
            >
              그룹 적용
            </button>
          </>
        ) : null}
        {onPin ? (
          <button type="button" className="btn" disabled={pending} onClick={onPin}>
            정비 PIN…
          </button>
        ) : null}
      </div>
    ) : null}
    {canEdit && storageShortfalls.length > 0 ? (
      <div className="px-4 pt-2">
        <StorageWarning shortfalls={storageShortfalls} />
      </div>
    ) : null}
    </>
  );
}

/**
 * 화면 추가 — **등록을 기다리는 화면을 골라** 등록한다. 코드를 옮겨 적는 길도 함께 둔다.
 *
 * 고르는 길을 먼저 두는 이유는 현장과 콘솔에 있는 사람이 다르기 때문이다. 코드 입력만
 * 있으면 여섯 자를 읽어 주는 통화가 대수만큼 필요하고, 그 사이 코드는 새것으로 바뀐다.
 *
 * 그래도 **입력 칸을 없애지 않는다.** 목록은 서버가 내줄 때만 있고(여러 조직이 한 서버를
 * 쓰면 내주지 않는다 — `routes/pairing.ts`), 옛 서버에는 그 경로가 아예 없다. 두 경우
 * 모두 목록 자리만 조용히 접히고 입력 칸이 남는다.
 */
function PairingModal({
  client,
  locations,
  onClose,
  onPaired,
}: {
  client: SignageOneClient;
  /** 등록되어 있는 위치 이름들. 현장에서 새 화면을 붙일 때 오타가 가장 많이 난다. */
  locations: string[];
  onClose: () => void;
  onPaired: () => void;
}): ReactNode {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  /** 이름을 목록이 채워 준 것인가 — 사람이 쓴 이름은 덮지 않는다. */
  const [namePicked, setNamePicked] = useState(true);
  const action = useAction();

  const pending = useAsync(() => client.listPendingPairings(), []);
  /*
   * 코드는 15분이면 새것으로 바뀐다(`PAIRING_TTL_MIN`). 목록이 그보다 느리면 이미 사라진
   * 코드를 고르게 되고, 방금 화면을 켠 사람은 자기 기기가 목록에 없다고 본다.
   */
  usePolling(pending.reload, 5_000);

  const now = new Date();
  const rows = (pending.data?.items ?? []).map((item) => ({ item, view: describePending(item, now) }));
  // 서버가 목록을 막았거나(여러 조직) 옛 서버라 경로가 없으면(오류) 자리를 접는다.
  const showList = pending.data?.listable === true;

  const pick = (item: PendingPairing): void => {
    setCode(item.code);
    if (namePicked) setName(suggestedName(item));
    action.clearError();
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    void action.run(
      () => client.claimPairing({ code: code.trim().toUpperCase(), name: name.trim(), location: location.trim() }),
      onPaired,
    );
  };

  return (
    <Modal title="화면 추가" onClose={onClose}>
      <form onSubmit={submit} className="form">
        {showList ? (
          <div className="pairing-pending">
            <div className="pairing-pending__head">
              <span className="pairing-pending__title">
                등록 대기 중인 화면
                <HelpTip
                  text="기기에서 플레이어를 실행해 등록 코드가 표시되면 여기에 나타납니다. 코드는 15분마다 바뀝니다."
                  label="등록 대기 도움말"
                />
              </span>
              <span className="hint">{rows.length > 0 ? `${rows.length}대` : ''}</span>
            </div>

            {rows.length === 0 ? (
              <p className="hint">등록 대기 중인 화면이 없습니다.</p>
            ) : (
              <ul className="pairing-pending__list">
                {rows.map(({ item, view }) => (
                  <li key={item.code}>
                    <button
                      type="button"
                      className={`pairing-pending__item${code === item.code ? ' is-picked' : ''}`}
                      onClick={() => pick(item)}
                      /* 고르는 사이에 만료된 줄. 다음 갱신에서 새 코드로 바뀐다. */
                      disabled={view.expiring && view.remaining === '만료됨'}
                    >
                      <span className="pairing-pending__code">{view.code}</span>
                      <span className="pairing-pending__detail">{view.detail}</span>
                      <span className={`pairing-pending__left${view.expiring ? ' is-soon' : ''}`}>
                        {view.remaining}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <p className="hint">
          {showList
            ? '목록에 없으면 화면에 표시된 6자리 코드를 입력하세요.'
            : '플레이어 화면에 표시된 6자리 코드를 입력하세요.'}
        </p>

        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

        <label className="field">
          <span>등록 코드</span>
          <input
            className="input input-code"
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="ABC234"
            maxLength={8}
            autoFocus
            required
          />
        </label>
        <label className="field">
          <span>화면 이름</span>
          <input
            className="input"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setNamePicked(false);
            }}
            placeholder="본사 1층 로비"
          />
        </label>
        <label className="field">
          <span>위치</span>
          {/*
            등록된 위치 중에서 고른다. 현장에서 새 화면을 붙일 때가 오타가 가장 많이
            나는 자리이고, 그렇게 갈라진 이름은 모니터링 벽에서 지점이 둘로 쪼개져 보인다.
          */}
          <LocationPicker
            value={location}
            known={locations}
            disabled={action.pending}
            onChange={setLocation}
          />
        </label>

        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending || code.length < 4}>
            {action.pending ? '등록 중…' : '등록'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
